import { FC, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userActions } from '@/redux/actions';
import { userSelectors } from '@/redux/selectors';
import { ConfirmEmailRequest } from '@/api';

import {
  AlertBox,
  AuthContainer,
  Button,
  Support,
  Suspense,
} from '@/components';

import styles from './ConfirmEmail.module.sass';

interface ConfirmEmailProps {}

const TOKEN_KEY = 'token';

const ConfirmEmail: FC<ConfirmEmailProps> = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isAuth = useAppSelector(userSelectors.getAuth);
  const { error, loading } = useAppSelector(userSelectors.getUserSlice);

  const token = searchParams.get(TOKEN_KEY);

  useEffect(() => {
    dispatch(userActions.setError(null));

    if (token) {
      const payload: ConfirmEmailRequest = {
        token,
      };

      dispatch(userActions.confirmEmail(payload));
    } else {
      dispatch(userActions.setError(t('translation.token_not_found_or_blank')));
    }
  }, []);

  const clickHandler = () => {
    dispatch(userActions.setError(null));
    navigate('/');
  };

  if (loading) {
    return <Suspense />;
  }

  if (error) {
    return (
      <AuthContainer
        title={t('translation.confirm_email_error_title')}
        alert={
          !!error && (
            <>
              <AlertBox type="danger">
                {{
                  title: t('translation.ooops'),
                  description: error,
                  icon: '🤔',
                }}
              </AlertBox>
              <AlertBox type="info">
                {{
                  title: t('translation.tips'),
                  description: (
                    <div className={styles.tips}>
                      <ul className={styles.list}>
                        {isAuth && (
                          <li>
                            <span className={styles.gap}>
                              {t(
                                'translation.tips_confirm_request_new_email_part1',
                              )}
                            </span>
                            <button
                              type="button"
                              className={`${styles.button} ${styles.gap}`}
                              onClick={clickHandler}
                            >
                              {t(
                                'translation.tips_confirm_request_new_email_part2',
                              )}
                            </button>
                            {t(
                              'translation.tips_confirm_request_new_email_part3',
                            )}
                          </li>
                        )}
                        {!isAuth && (
                          <li>{t('translation.tips_confirm_try_to_login')}</li>
                        )}

                        <li>
                          <div className={styles.wrapper}>
                            <span className={styles.gap}>
                              {t('translation.contact_support')}
                            </span>
                            <Support />
                          </div>
                        </li>
                      </ul>
                    </div>
                  ),
                }}
              </AlertBox>
            </>
          )
        }
        buttons={
          <div>
            <Button
              size="m"
              minWidth={240}
              corner="rect"
              onClick={clickHandler}
            >
              {t('translation.ok')}
            </Button>
          </div>
        }
      />
    );
  }

  return null;
};

export default ConfirmEmail;
